import type { BadgeTono, OpcionSelect } from "@/components";
import type { AsignacionPago, EstadoGasto, FormaPago, Gasto, GastoDetalle, ListadoGastos, MedioPago, MedioPagoGasto, Moneda, Pago, Proveedor, Ref, TipoGasto, TotalesGastos, TotalesParte } from "./api";
import { formatMoneda, hoyISO } from "./format";
import { CIEN, aEditable, aEscalado, aTextoApi, partesIguales } from "./participacion";
import { SIN_VALOR, TODOS_LOS_RUBROS } from "./proveedores";

/** Una sola constante: el filtro de rubro usa las opciones de `opcionesFiltroRubro` (`TODOS_LOS_RUBROS`). */
export const TODOS = TODOS_LOS_RUBROS;

/** Tres estados, fee incluido (Mati, 2026-11-02). */
export const ETIQUETA_ESTADO: Record<EstadoGasto, string> = { pendiente: "Impago", parcial: "Pago parcial", pagado: "Pagado" };
export const TONO_ESTADO: Record<EstadoGasto, BadgeTono> = { pendiente: "alerta", parcial: "primario", pagado: "exito" };
export const ETIQUETA_MEDIO: Record<MedioPago, string> = { transferencia: "Transferencia", efectivo: "Efectivo", cheque: "Cheque", otro: "Otro" };

const opciones = <T extends string>(etiquetas: Record<T, string>): OpcionSelect[] =>
  (Object.keys(etiquetas) as T[]).map((v) => ({ valor: v, etiqueta: etiquetas[v] }));

export const OPCIONES_MEDIO = opciones(ETIQUETA_MEDIO);
export const ETIQUETA_MEDIO_GASTO: Record<MedioPagoGasto, string> = { ...ETIQUETA_MEDIO, cuenta_corriente: "Cuenta corriente" };
export const OPCIONES_TIPO: OpcionSelect[] = [{ valor: "A", etiqueta: "A" }, { valor: "B", etiqueta: "B" }];
export const OPCIONES_ESTADO = opciones(ETIQUETA_ESTADO);
export const OPCIONES_MONEDA: OpcionSelect[] = [{ valor: "ARS", etiqueta: "ARS" }, { valor: "USD", etiqueta: "USD" }];
const conTodos = (etiqueta: string, lista: OpcionSelect[]) => [{ valor: TODOS, etiqueta }, ...lista];
export const OPCIONES_FILTRO_TIPO = conTodos("A y B", OPCIONES_TIPO);
export const OPCIONES_FILTRO_ESTADO = conTodos("Todos los estados", OPCIONES_ESTADO);

/** Un gasto común sin proveedor es un pago a cuenta o un gasto de la compra cargado desde la ficha del terreno. */
export const RUTA_COMPRA_TERRENO = "/gastos/compra-terreno";

/** La ficha de una fila del listado: la compra del terreno tiene la suya. */
export function rutaFicha(g: Gasto): string {
  return g.compra_terreno ? RUTA_COMPRA_TERRENO : `/gastos/${g.id}`;
}

/** Bajo "Compra del terreno" en el listado: los pagos a cuenta van dentro, no sueltos. */
export function textoPagosACuenta(cantidad: number): string {
  if (cantidad === 0) return "Precio acordado · sin pagos a cuenta";
  return `Precio acordado · ${cantidad} ${cantidad === 1 ? "pago" : "pagos"} a cuenta`;
}

export function esDeLaCompraDelTerreno(g: Gasto): boolean {
  return !g.es_fee && g.proveedor === null;
}

export function nombreProveedor(g: Gasto): string {
  if (g.proveedor) return g.proveedor.nombre;
  if (g.compra_terreno) return "—";
  return g.es_fee ? "Administración" : "Compra del terreno";
}

export function opcionesProveedor(lista: Proveedor[], actualId?: number | null): OpcionSelect[] {
  return lista
    .filter((p) => p.activo || p.id === actualId)
    .map((p) => ({ valor: String(p.id), etiqueta: p.activo ? p.nombre : `${p.nombre} (archivado)` }));
}

// ------------------------------------------------------------------ montos (siempre texto, nunca float)

/** "1.234,56" | "1234.56" | "1234,56" → "1234.56". null si no es un monto válido mayor a cero (hasta 2 decimales). */
export function parseMonto(texto: string): string | null {
  let t = texto.trim();
  if (!t) return null;
  const coma = t.lastIndexOf(",");
  const punto = t.lastIndexOf(".");
  if (coma >= 0 && punto >= 0) {
    if (coma > punto) t = t.replace(/\./g, "").replace(",", ".");
    else t = t.replace(/,/g, "");
  } else if (coma >= 0) {
    t = t.replace(",", ".");
  } else if (punto >= 0 && (t.match(/\./g) ?? []).length > 1) {
    return null;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  if (/^0+(\.0{1,2})?$/.test(t)) return null;
  return t;
}

/** Monto para editar en un input: "1500.50" → "1500,50"; "250.00" → "250" (decimales en cero se descartan, el resto se conserva tal cual). */
export function montoAInput(monto: string): string {
  const [ent = "", dec = ""] = monto.split(".");
  return /^0*$/.test(dec) ? ent : `${ent},${dec}`;
}

/** "1234.5" → 123450n (centavos, sin pasar por float). */
export const aCentavos = (decimal: string): bigint => {
  const [ent = "0", dec = ""] = decimal.split(".");
  return BigInt(ent) * 100n + BigInt((dec + "00").slice(0, 2));
};
export const deCentavos = (c: bigint): string => `${c / 100n}.${String(c % 100n).padStart(2, "0")}`;

/** Vista previa del fee: monto × % / 100 en centavos, redondeo bancario (igual que el backend). */
export function feePrevisto(montoTexto: string, porcentaje: string): string | null {
  const monto = parseMonto(montoTexto);
  const pct = parseMonto(porcentaje);
  if (monto === null || pct === null) return null;
  const num = aCentavos(monto) * aCentavos(pct); // centavos × (% en centésimas) → dividir por 10000
  let fee = num / 10000n;
  const resto = num % 10000n;
  if (resto > 5000n || (resto === 5000n && fee % 2n === 1n)) fee += 1n;
  return fee === 0n ? null : deCentavos(fee);
}

/** Etiqueta del fee previsto en el formulario: "Fee 15 %: $ 150.000,00". */
export function etiquetaFee(monto: string, porcentaje: string, moneda: Moneda): string {
  return `Fee ${porcentaje.replace(".00", "").replace(".", ",")} %: ${formatMoneda(monto, moneda)}`;
}

/** Tipo de cambio para el input: "1450.0000" → "1450"; "1450.5000" → "1450,5000" (decimales en cero se descartan, el resto se conserva tal cual). */
export function tipoCambioAInput(tc: string | null | undefined): string {
  return tc ? montoAInput(tc) : "";
}

/**
 * "1450,5" → "1450.5"; "1.450,25" → "1450.25" (punto de miles + coma decimal), con hasta 4 decimales.
 * Mismo criterio que `parseMonto` para separadores, salvo un caso propio: un solo punto seguido de
 * exactamente 3 dígitos y sin coma es ambiguo (¿miles o decimales?) — "1.450" se rechaza en vez de leerse como 1,45.
 * null si no es válido o no es mayor a cero.
 */
export function parseTipoCambio(texto: string): string | null {
  let t = texto.trim();
  if (!t) return null;
  const coma = t.lastIndexOf(",");
  const punto = t.lastIndexOf(".");
  if (coma >= 0 && punto >= 0) {
    if (coma > punto) t = t.replace(/\./g, "").replace(",", ".");
    else t = t.replace(/,/g, "");
  } else if (coma >= 0) {
    t = t.replace(",", ".");
  } else if (punto >= 0) {
    const puntos = t.match(/\./g) ?? [];
    if (puntos.length > 1) return null;
    if (t.length - punto - 1 === 3) return null; // "1.450": ambiguo entre miles y decimales
  }
  if (!/^\d+(\.\d{1,4})?$/.test(t) || Number(t) === 0) return null;
  return t;
}

// ------------------------------------------------------------------ filtros

export interface FiltrosGastos {
  desde: string;
  hasta: string;
  proveedorId: string;
  rubroId: string;
  tipo: string;
  estado: string;
  q: string;
}

export const filtrosIniciales = (): FiltrosGastos => ({ desde: "", hasta: "", proveedorId: TODOS, rubroId: TODOS, tipo: TODOS, estado: TODOS, q: "" });

export function queryGastos(f: FiltrosGastos): string {
  const p = new URLSearchParams();
  if (f.desde) p.set("desde", f.desde);
  if (f.hasta) p.set("hasta", f.hasta);
  if (f.proveedorId !== TODOS) p.set("proveedor_id", f.proveedorId);
  if (f.rubroId !== TODOS) p.set("rubro_id", f.rubroId);
  if (f.tipo !== TODOS) p.set("tipo", f.tipo);
  if (f.estado !== TODOS) p.set("estado", f.estado);
  if (f.q.trim()) p.set("q", f.q.trim());
  const s = p.toString();
  return s ? `?${s}` : "";
}

// ------------------------------------------------------------------ formulario de gasto

export interface FormGasto {
  fecha: string;
  proveedor_id: string;
  rubro_id: string;
  tipo: TipoGasto;
  descripcion: string;
  comprobante_nro: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string;
  lleva_fee: boolean;
  /** Presupuesto aceptado, pago de su plan e ítem del rubro (lib/facturas.ts); "" = sin vincular. */
  cotizacion_id: string;
  cuota_id: string;
  item_id: string;
}

export type ErroresGasto = Partial<Record<"proveedor_id" | "rubro_id" | "descripcion" | "monto" | "tipo_cambio" | "comprobante_nro" | "fecha", string>>;

export interface PayloadGasto {
  fecha: string;
  proveedor_id: number;
  rubro_id: number;
  tipo: TipoGasto;
  descripcion: string;
  comprobante_nro: string | null;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  lleva_fee: boolean;
  cotizacion_id: number | null;
  cuota_id: number | null;
  presupuesto_item_id: number | null;
}

const idOVacio = (valor: string): number | null => (valor !== "" && valor !== SIN_VALOR ? Number(valor) : null);

export function formGasto(g?: Gasto, hoy = hoyISO(), tcSugerido?: string | null): FormGasto {
  if (!g) {
    return { fecha: hoy, proveedor_id: "", rubro_id: "", tipo: "A", descripcion: "", comprobante_nro: "", monto: "", moneda: "ARS", tipo_cambio: tipoCambioAInput(tcSugerido), lleva_fee: true, cotizacion_id: "", cuota_id: "", item_id: "" };
  }
  return {
    fecha: g.fecha, proveedor_id: g.proveedor ? String(g.proveedor.id) : "", rubro_id: String(g.rubro.id), tipo: g.tipo,
    descripcion: g.descripcion, comprobante_nro: g.comprobante_nro ?? "", monto: montoAInput(g.monto), moneda: g.moneda,
    tipo_cambio: tipoCambioAInput(g.tipo_cambio), lleva_fee: g.lleva_fee,
    cotizacion_id: g.cotizacion ? String(g.cotizacion.id) : "", cuota_id: g.cuota ? String(g.cuota.id) : "", item_id: g.item ? String(g.item.id) : "",
  };
}

export function validarGasto(f: FormGasto): ErroresGasto {
  const e: ErroresGasto = {};
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (!f.proveedor_id || f.proveedor_id === SIN_VALOR) e.proveedor_id = "Elegí un proveedor";
  if (!f.rubro_id || f.rubro_id === SIN_VALOR) e.rubro_id = "Elegí un rubro";
  if (!f.descripcion.trim()) e.descripcion = "Este dato es obligatorio";
  else if (f.descripcion.trim().length > 200) e.descripcion = "Hasta 200 caracteres";
  if (f.comprobante_nro.trim().length > 40) e.comprobante_nro = "Hasta 40 caracteres";
  if (parseMonto(f.monto) === null) e.monto = "Ingresá un monto mayor a cero";
  if (f.moneda === "ARS" && parseTipoCambio(f.tipo_cambio) === null) e.tipo_cambio = "Ingresá el tipo de cambio";
  return e;
}

export function aPayloadGasto(f: FormGasto): PayloadGasto {
  return {
    fecha: f.fecha, proveedor_id: Number(f.proveedor_id), rubro_id: Number(f.rubro_id), tipo: f.tipo, descripcion: f.descripcion.trim(),
    comprobante_nro: f.comprobante_nro.trim() || null, monto: parseMonto(f.monto) ?? "0", moneda: f.moneda,
    tipo_cambio: f.moneda === "ARS" ? parseTipoCambio(f.tipo_cambio) : null, lleva_fee: f.lleva_fee,
    cotizacion_id: idOVacio(f.cotizacion_id), cuota_id: idOVacio(f.cuota_id), presupuesto_item_id: idOVacio(f.item_id),
  };
}

// ------------------------------------------------------------------ formulario de pago

export interface FormPago {
  fecha: string;
  monto: string;
  tipo_cambio: string;
  medio: MedioPagoGasto;
  nota: string;
  /** id de la caja como texto; "" = la principal (no se manda). */
  caja_id: string;
  /** A quién se le acredita, si el medio es cuenta corriente. */
  asignaciones: LineaAsignacion[];
}

export type ErroresPago = Partial<Record<"fecha" | "monto" | "tipo_cambio" | "nota" | "asignaciones", string>>;

export interface PayloadPago {
  fecha: string;
  monto: string;
  tipo_cambio: string | null;
  medio: MedioPagoGasto;
  nota: string | null;
  caja_id?: number;
  asignaciones: { fiduciante_id: number | null; grupo_id: number | null; porcentaje: string }[];
}

/** Sin pago: uno nuevo, con a quién se acreditó el último por cuenta corriente a ese proveedor (`sugerida`). */
export function formPago(p?: Pago, hoy = hoyISO(), tcSugerido?: string | null, cajaDefecto = "", sugerida: AsignacionPago[] = []): FormPago {
  if (!p) return { fecha: hoy, monto: "", tipo_cambio: tipoCambioAInput(tcSugerido), medio: "transferencia", nota: "", caja_id: cajaDefecto, asignaciones: lineasAsignacion(sugerida) };
  return {
    fecha: p.fecha, monto: montoAInput(p.monto), tipo_cambio: tipoCambioAInput(p.tipo_cambio), medio: p.medio, nota: p.nota ?? "", caja_id: String(p.caja_id),
    asignaciones: lineasAsignacion(p.asignaciones.length ? p.asignaciones : sugerida),
  };
}

/**
 * `gasto`: el monto escrito es el total, fee incluido (`partirPago`), y no puede pasar lo que falta. `excluir`: al
 * editar, el monto actual del pago.
 */
export function validarPago(f: FormPago, moneda: Moneda, gasto?: GastoParaPago, excluir?: string): ErroresPago {
  const e: ErroresPago = {};
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (parseMonto(f.monto) === null) e.monto = "Ingresá un monto mayor a cero";
  else if (gasto) {
    const error = errorTotal(f.monto, gasto, moneda, excluir);
    if (error) e.monto = error;
  }
  if (moneda === "ARS" && parseTipoCambio(f.tipo_cambio) === null) e.tipo_cambio = "Ingresá el tipo de cambio";
  if (f.nota.trim().length > 200) e.nota = "Hasta 200 caracteres";
  const asignaciones = f.medio === "cuenta_corriente" ? validarAsignaciones(f.asignaciones) : null;
  if (asignaciones) e.asignaciones = asignaciones;
  return e;
}

/** Con `gasto`, el monto que se manda es la parte del proveedor del total escrito (el fee lo arma el servidor). */
export function aPayloadPago(f: FormPago, moneda: Moneda, gasto?: GastoParaPago, excluir?: string): PayloadPago {
  const cc = f.medio === "cuenta_corriente";
  const monto = gasto ? (partirPago(f.monto, gasto, excluir)?.proveedor ?? "0") : (parseMonto(f.monto) ?? "0");
  return {
    fecha: f.fecha, monto, tipo_cambio: moneda === "ARS" ? parseTipoCambio(f.tipo_cambio) : null, medio: f.medio, nota: f.nota.trim() || null,
    // por cuenta corriente no sale plata: la caja no se pregunta
    ...(f.caja_id && !cc ? { caja_id: Number(f.caja_id) } : {}),
    asignaciones: cc ? f.asignaciones.map((l) => ({ fiduciante_id: l.fiduciante_id, grupo_id: l.grupo_id, porcentaje: aTextoApi(aEscalado(l.porcentaje) ?? 0) })) : [],
  };
}

// ------------------------------------------------------------------ pago por cuenta corriente (sólo gastos B)

/** Un gasto B (no su fee) se puede pagar acreditándolo en la cuenta corriente de fiduciantes o grupos. */
export function admiteCuentaCorriente(g: Pick<Gasto, "tipo" | "es_fee">): boolean {
  return g.tipo === "B" && !g.es_fee;
}

/** En el desplegable dice para qué sirve (Mati, 2026-10-25): un fiduciante que pagó de su bolsillo (o que es el
 * proveedor) queda con un aporte por el pago, y el gasto, pagado. En las listas sigue diciendo "Cuenta corriente". */
const OPCIONES_MEDIO_GASTO = opciones({ ...ETIQUETA_MEDIO_GASTO, cuenta_corriente: "Lo pagó un fiduciante (cuenta corriente)" });

export function opcionesMedioPago(g: Pick<Gasto, "tipo" | "es_fee">): OpcionSelect[] {
  return admiteCuentaCorriente(g) ? OPCIONES_MEDIO_GASTO : OPCIONES_MEDIO;
}

/** "Cuenta corriente · Beto 60 %, Caro 40 %" (o sólo el medio). */
export function textoMedio(p: Pick<Pago, "medio" | "asignaciones">): string {
  if (p.medio !== "cuenta_corriente" || p.asignaciones.length === 0) return ETIQUETA_MEDIO_GASTO[p.medio];
  return `${ETIQUETA_MEDIO_GASTO[p.medio]} · ${p.asignaciones.map((a) => `${a.nombre} ${aEditable(aEscalado(a.porcentaje) ?? 0)} %`).join(", ")}`;
}

export interface LineaAsignacion {
  /** "f:3" o "g:7": un fiduciante o un grupo. */
  clave: string;
  fiduciante_id: number | null;
  grupo_id: number | null;
  nombre: string;
  /** Texto como lo escribe el admin ("50", "33,3334"). */
  porcentaje: string;
}

export const claveFiduciante = (id: number) => `f:${id}`;
export const claveGrupo = (id: number) => `g:${id}`;

export function lineasAsignacion(asignaciones: AsignacionPago[]): LineaAsignacion[] {
  return asignaciones.map((a) => ({
    clave: a.grupo_id !== null ? claveGrupo(a.grupo_id) : claveFiduciante(a.fiduciante_id ?? 0), fiduciante_id: a.fiduciante_id, grupo_id: a.grupo_id,
    nombre: a.nombre, porcentaje: aEditable(aEscalado(a.porcentaje) ?? 0),
  }));
}

type Nombrado = { id: number; nombre: string };

export function opcionesAsignacion(fiduciantes: Nombrado[], grupos: Nombrado[], lineas: LineaAsignacion[]): OpcionSelect[] {
  const usadas = new Set(lineas.map((l) => l.clave));
  return [
    ...fiduciantes.map((f) => ({ valor: claveFiduciante(f.id), etiqueta: f.nombre })),
    ...grupos.map((g) => ({ valor: claveGrupo(g.id), etiqueta: `${g.nombre} (grupo)` })),
  ].filter((o) => !usadas.has(o.valor));
}

/** Agrega al elegido (por su clave) y reparte en partes iguales; el admin corrige los % después. */
export function agregarAsignacion(lineas: LineaAsignacion[], clave: string, fiduciantes: Nombrado[], grupos: Nombrado[]): LineaAsignacion[] {
  if (lineas.some((l) => l.clave === clave)) return lineas;
  const f = fiduciantes.find((x) => claveFiduciante(x.id) === clave);
  const g = grupos.find((x) => claveGrupo(x.id) === clave);
  if (!f && !g) return lineas;
  const nueva: LineaAsignacion = f
    ? { clave, fiduciante_id: f.id, grupo_id: null, nombre: f.nombre, porcentaje: "" }
    : { clave, fiduciante_id: null, grupo_id: g!.id, nombre: g!.nombre, porcentaje: "" };
  return partesIguales([...lineas, nueva]);
}

export function quitarAsignacion(lineas: LineaAsignacion[], clave: string): LineaAsignacion[] {
  return partesIguales(lineas.filter((l) => l.clave !== clave));
}

export function cambiarPorcentajeAsignacion(lineas: LineaAsignacion[], clave: string, porcentaje: string): LineaAsignacion[] {
  return lineas.map((l) => (l.clave === clave ? { ...l, porcentaje } : l));
}

/** null si está bien. Mismos textos que el backend (`app/gastos/cuenta_corriente.py`) donde se puede. */
export function validarAsignaciones(lineas: LineaAsignacion[]): string | null {
  if (lineas.length === 0) return "Elegí a quién se le acredita el pago";
  let total = 0;
  for (const l of lineas) {
    const e = aEscalado(l.porcentaje);
    if (e === null || e === 0 || e > CIEN) return `El % de ${l.nombre} tiene que ser mayor que 0 y hasta 100`;
    total += e;
  }
  if (total !== CIEN) return `Los % del pago suman ${aEditable(total)} %: tienen que sumar 100`;
  return null;
}

// ------------------------------------------------------------------ borrado

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/** Cuenta pagos y adjuntos propios más los del fee (el fee tiene sus propios pagos y adjuntos, ver ResumenFee). */
export function mensajeBorrado(g: GastoDetalle): string {
  const pagos = g.cantidad_pagos + (g.fee?.cantidad_pagos ?? 0);
  const adjuntos = g.cantidad_adjuntos + (g.fee?.cantidad_adjuntos ?? 0);
  const partes: string[] = [];
  if (pagos > 0) partes.push(plural(pagos, "pago", "pagos"));
  if (adjuntos > 0) partes.push(plural(adjuntos, "adjunto", "adjuntos"));
  if (g.fee) partes.push("el fee de administración");
  if (partes.length === 0) return "¿Borrar este gasto? No se puede deshacer.";
  const lista = partes.length === 1 ? partes[0] : `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
  return `¿Borrar este gasto? Se borran también ${lista}. No se puede deshacer.`;
}

// ------------------------------------------------------------------ fiduciante

const porNombre = (a: Ref, b: Ref) => a.nombre.localeCompare(b.nombre, "es");

/** Proveedores y rubros que aparecen en los gastos (sin repetir, por nombre): los filtros del fiduciante, que no lee la agenda ni los rubros. */
export function refsDeGastos(items: Pick<Gasto, "proveedor" | "rubro">[]): { proveedores: Ref[]; rubros: Ref[] } {
  const proveedores = new Map<number, Ref>();
  const rubros = new Map<number, Ref>();
  for (const g of items) {
    if (g.proveedor) proveedores.set(g.proveedor.id, g.proveedor);
    rubros.set(g.rubro.id, g.rubro);
  }
  return { proveedores: [...proveedores.values()].sort(porNombre), rubros: [...rubros.values()].sort(porNombre) };
}

/**
 * Terreno y obra por separado, arriba en Gastos (Mati, 2026-11-03: a futuro importa cuánto se pagó el terreno y
 * cuánto se gastó en la obra). Sin la compra del terreno en el listado, null: quedan las tres tarjetas de siempre.
 */
export function tarjetasTerrenoYObra(totales: TotalesGastos): { terreno: { valor: string; detalle: string }; obra: { valor: string; detalle: string } } | null {
  if (!totales.terreno) return null;
  const tarjeta = (p: TotalesParte) => ({ valor: formatMoneda(p.monto_usd, "USD"), detalle: `Pagado ${formatMoneda(p.pagado_usd, "USD")}` });
  return { terreno: tarjeta(totales.terreno), obra: tarjeta(totales.obra) };
}

/** Tarjeta "Gastos de la obra" del Inicio del fiduciante: el total en USD, lo pagado y cuántos gastos (sin contar los fees). */
export function tarjetaGastos(listado: ListadoGastos): { valor: string; detalle: string } {
  const n = listado.items.filter((g) => !g.es_fee).length;
  if (n === 0) return { valor: formatMoneda(0, "USD"), detalle: "Todavía no hay gastos cargados" };
  // Lo pagado en grande: es con lo que se calcula el saldo de cada fiduciante, que está al lado (Mati, 2026-11-04).
  const t = listado.totales;
  const falta = parseMonto(t.saldo_usd) !== null ? `comprometido ${formatMoneda(t.monto_usd, "USD")} · falta pagar ${formatMoneda(t.saldo_usd, "USD")}` : "todo lo comprometido";
  return { valor: formatMoneda(t.pagado_usd, "USD"), detalle: `Pagado · ${falta}` };
}

/**
 * El gasto con su fee sumado, como lo ven todos (Mati, 2026-10-20: "ya mostraría 6.900"). Con el proveedor saldado
 * está pagado entero: el fee se generó con ese pago, y el A por cobrar no lo deja en "Pago parcial" (Mati,
 * 2026-11-02). Antes, lo pagado del gasto y del fee contra el total. Sin fee, el gasto tal cual.
 */
export function montosConFee(g: Pick<Gasto, "monto" | "pagado" | "saldo" | "estado" | "fee">): { monto: string; pagado: string; saldo: string; estado: EstadoGasto; incluyeFee: boolean } {
  if (!g.fee) return { monto: g.monto, pagado: g.pagado, saldo: g.saldo, estado: g.estado, incluyeFee: false };
  const total = aCentavos(g.monto) + aCentavos(g.fee.monto);
  if (aCentavos(g.saldo) <= 0n) return { monto: deCentavos(total), pagado: deCentavos(total), saldo: "0.00", estado: "pagado", incluyeFee: true };
  const pagado = aCentavos(g.pagado) + aCentavos(g.fee.pagado);
  const estado: EstadoGasto = pagado > 0n ? "parcial" : "pendiente";
  return { monto: deCentavos(total), pagado: deCentavos(pagado), saldo: deCentavos(total - pagado), estado, incluyeFee: true };
}

/**
 * La fila no debe nada (Mati, 2026-10-29: el cuadro la pinta): pagada (el proveedor saldado, `montosConFee`) y con su comprobante. La
 * compra del terreno no tiene comprobante propio (los recibos son de sus pagos) y el fiduciante no ve si falta.
 */
export function gastoCompleto(g: Pick<Gasto, "monto" | "pagado" | "saldo" | "estado" | "fee" | "tiene_comprobante" | "compra_terreno">, esFiduciante: boolean): boolean {
  if (montosConFee(g).estado !== "pagado") return false;
  return g.tiene_comprobante || !!g.compra_terreno || esFiduciante;
}

export const ETIQUETA_FORMA_PAGO: Record<FormaPago, string> = { caja: "Caja", cuenta_corriente: "Cta. cte.", mixta: "Caja y cta. cte." };

/** Columna "Forma de pago": de la caja o de la cuenta corriente de fiduciantes, según sus pagos; sin pagos, "—". */
export function textoFormaPago(forma: FormaPago | null): string {
  return forma ? ETIQUETA_FORMA_PAGO[forma] : "—";
}

/** Al revés que antes (Mati, 2026-11-02): lo normal es que el gasto lleve fee y no se marca; se marca lo que no lo
 * lleva (la compra del terreno, un gasto sin fee). */
export const TEXTO_SIN_FEE = "Sin fee";

/** La marca "Sin fee": un gasto común (ni el fee mismo) que no suma fee. */
export function sinFee(g: Pick<Gasto, "fee" | "es_fee">): boolean {
  return !g.es_fee && !g.fee;
}

// ------------------------------------------------------------------ pago con el fee incluido

/**
 * Mati, 2026-11-02: el pago se carga por el total, fee incluido (lo pagan todos los fiduciantes), y el fee se genera
 * **entero con el pago que salda al proveedor**: los pagos parciales van enteros al proveedor. El que salda lleva el
 * saldo más el fee entero (el B se le acredita solo a los fiduciarios; el A queda por cobrar hasta que se liquida en
 * Honorarios). Como `app/honorarios/reglas.py`.
 */
type GastoParaPago = Pick<Gasto, "monto" | "pagado" | "saldo" | "fee" | "es_fee">;
type PagoParaTotal = Pick<Pago, "id" | "fecha" | "monto">;

function feeDe(g: GastoParaPago): bigint | null {
  return !g.es_fee && g.fee ? aCentavos(g.fee.monto) : null;
}

/** El gasto sin un pago de `monto` (el que se está editando): lo suyo vuelve a estar disponible. */
function sinPago(g: GastoParaPago, monto?: string): GastoParaPago {
  if (!monto) return g;
  const m = aCentavos(monto);
  return { ...g, pagado: deCentavos(aCentavos(g.pagado) - m), saldo: deCentavos(aCentavos(g.saldo) + m) };
}

/** Lo que falta pagar: el saldo más el fee entero (lo lleva el pago que salda). Sin fee (o en el fee mismo), el saldo. */
export function totalAPagar(g: GastoParaPago): string {
  const fee = feeDe(g);
  const saldo = aCentavos(g.saldo);
  if (fee === null) return g.saldo;
  return saldo <= 0n ? "0.00" : deCentavos(saldo + fee);
}

/**
 * El total escrito, partido entre el proveedor y el fee: menos que el saldo es un pago parcial (todo al proveedor);
 * el saldo más el fee, el que salda. `excluir`: al editar, el monto que el pago ya tiene (vuelve a estar disponible).
 * null si no es un monto, supera lo que falta o queda en el medio (`validarPago` dice cuál).
 */
export function partirPago(totalTexto: string, gasto: GastoParaPago, excluir?: string): { proveedor: string; fee: string } | null {
  const texto = parseMonto(totalTexto);
  if (texto === null) return null;
  const g = sinPago(gasto, excluir);
  const total = aCentavos(texto);
  const saldo = aCentavos(g.saldo);
  const fee = feeDe(g);
  if (fee === null) return total > saldo ? null : { proveedor: deCentavos(total), fee: "0.00" };
  if (total < saldo) return { proveedor: deCentavos(total), fee: "0.00" };
  if (total === saldo + fee) return { proveedor: deCentavos(saldo), fee: deCentavos(fee) };
  return null;
}

/** El aviso "El fee se genera cuando termines de pagar el gasto": un pago parcial de un gasto con fee. */
export function pagoParcialConFee(partes: { proveedor: string; fee: string } | null, g: GastoParaPago): boolean {
  return partes !== null && feeDe(g) !== null && partes.fee === "0.00";
}

export const AVISO_FEE_AL_SALDAR = "El fee se genera cuando termines de pagar el gasto.";

/** Por qué no sirve el total escrito (null si sirve o no es un monto: eso lo dice `validarPago`). */
function errorTotal(totalTexto: string, gasto: GastoParaPago, moneda: Moneda, excluir?: string): string | null {
  if (partirPago(totalTexto, gasto, excluir) !== null) return null;
  const g = sinPago(gasto, excluir);
  const total = aCentavos(parseMonto(totalTexto) ?? "0");
  const conFee = totalAPagar(g);
  if (total > aCentavos(conFee)) return `Supera lo que falta pagar (${formatMoneda(conFee, moneda)})`;
  return `Para terminar de pagar el gasto son ${formatMoneda(conFee, moneda)} con el fee; un pago parcial es de menos de ${formatMoneda(g.saldo, moneda)}`;
}

/** Para la lista de pagos: cada pago con su total y su fee, por fecha (el fee entero en el que saldó). Vacío sin fee. */
export function totalesDePagos(g: GastoParaPago, pagos: PagoParaTotal[]): Map<number, { total: string; fee: string }> {
  const fee = feeDe(g);
  const r = new Map<number, { total: string; fee: string }>();
  if (fee === null) return r;
  const monto = aCentavos(g.monto);
  let antes = 0n;
  for (const p of [...pagos].sort((a, b) => a.fecha.localeCompare(b.fecha) || a.id - b.id)) {
    const parte = aCentavos(p.monto);
    const suyo = antes < monto && antes + parte >= monto ? fee : 0n;
    r.set(p.id, { total: deCentavos(parte + suyo), fee: deCentavos(suyo) });
    antes += parte;
  }
  return r;
}

/** El total (con su fee, si fue el que saldó) de un pago ya cargado, para editarlo. */
export function totalDePago(g: GastoParaPago, pagos: PagoParaTotal[], pago: PagoParaTotal): string {
  return totalesDePagos(g, pagos).get(pago.id)?.total ?? pago.monto;
}
